import { useEffect } from "react";
import { alpha, createTheme } from "@mui/material/styles";

// The buyer-facing Trust Center (/trust/<slug> and custom domains) has its
// own look; the admin keeps the app theme. Everything here is applied only
// inside TrustCenterPage (PublicThemeProvider), so admin screens never see it.
//
// The company's branding (Overview → Branding in the admin, see
// data/brand.js) sets `accent` (primary: buttons, links), `secondary`
// (highlights: selected tab, checkboxes, counts) and the font. Everything
// else is neutral on purpose.

// Geist: crisp at the small sizes this page uses a lot (13–14px captions,
// tables) and steady figures for the counts. The system UI font stands in
// while it loads.
export const PUBLIC_FONT =
  'Geist, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif';
const FONT_URL =
  "https://fonts.googleapis.com/css2?family=Geist:wght@400;500;600;700&display=swap";

// Applied to the page and to its dialogs (rendered outside the page box), so
// text that is not an MUI Typography uses the same font and rendering.
export const fontRenderingWith = (fontFamily) => ({
  fontFamily,
  WebkitFontSmoothing: "antialiased",
  MozOsxFontSmoothing: "grayscale",
  textRendering: "optimizeLegibility",
});
export const fontRendering = fontRenderingWith(PUBLIC_FONT);

export const DEFAULT_ACCENT = "#3B4CE0";

// Type: semibold (600) only for the navigation bar and headings; names and
// labels are medium (500) in `inkName`, a softer ink than the headings;
// everything else is regular.
export const tokens = (accent = DEFAULT_ACCENT, secondary = accent, font = PUBLIC_FONT) => ({
  font,
  ink: "#121A3A",
  inkName: "#2B3453",
  inkSoft: "#4A5372",
  muted: "#6B7390",
  paper: "#F5F6FA",
  surface: "#FFFFFF",
  line: "#E4E7F0",
  lineStrong: "#D3D8E6",
  accent,
  accentSoft: alpha(accent, 0.08),
  secondary,
  secondarySoft: alpha(secondary, 0.1),
  verified: "#0E7A55",
  verifiedSoft: "#E6F4EE",
  attention: "#B45309",
  attentionSoft: "#FDF1E2",
  radius: { container: 16, control: 10, pill: 999 },
  // One easing for everything that moves.
  ease: "cubic-bezier(0.22, 1, 0.36, 1)",
});

// Loads a font stylesheet once (Geist by default), only when the public page
// is shown. `id` keeps each font to one <link>.
export const usePublicFont = (url = FONT_URL, id = "trust-center-font") => {
  useEffect(() => {
    if (!url || document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = url;
    document.head.appendChild(link);
  }, [url, id]);
};

// The app theme with the public page's type, colours and quieter components.
// `brand`: { accent, secondary, fontFamily } (all optional).
export const createPublicTheme = (outer, brand = {}) => {
  const t = tokens(
    brand.accent || DEFAULT_ACCENT,
    brand.secondary || brand.accent || DEFAULT_ACCENT,
    brand.fontFamily || PUBLIC_FONT
  );
  const font = t.font;
  return createTheme(outer, {
    palette: {
      primary: { main: t.accent },
      text: { primary: t.inkName, secondary: t.muted },
      divider: t.line,
      background: { default: t.paper, paper: t.surface },
    },
    shape: { borderRadius: t.radius.control },
    typography: {
      fontFamily: font,
      h1: { fontFamily: font },
      h2: { fontFamily: font },
      h3: { fontFamily: font },
      h4: { fontFamily: font },
      h5: { fontFamily: font, fontWeight: 600, letterSpacing: "-0.01em" },
      h6: { fontFamily: font, fontWeight: 600, letterSpacing: "-0.01em" },
      body1: { fontFamily: font, fontSize: 15, lineHeight: 1.6 },
      body2: { fontFamily: font, fontSize: 14, lineHeight: 1.55 },
      caption: { fontFamily: font },
      button: { fontFamily: font, textTransform: "none", fontWeight: 500 },
      subtitle1: { fontFamily: font },
      subtitle2: { fontFamily: font },
    },
    // Components that read theme.customColors pick up the public palette.
    customColors: {
      text: { primary: t.inkName, strong: t.inkName, secondary: t.muted, muted: t.muted },
      background: {
        main: t.surface,
        header: t.paper,
        cardHeader: t.surface,
        light: t.paper,
        infoBox: t.paper,
        hover: alpha(t.ink, 0.03),
      },
      border: { main: t.line, subtle: t.line, strong: t.lineStrong, dashed: t.lineStrong },
      icons: { primary: t.accent, secondary: t.muted },
      status: {
        success: { main: t.verified, text: t.verified, bg: t.verifiedSoft },
        warning: { main: t.attention, text: t.attention, bg: t.attentionSoft },
        error: { main: "#C2412D", text: "#B4321F", bg: "#FCEDEA" },
      },
    },
    trust: t,
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: t.radius.container,
            boxShadow: "none",
            border: `1px solid ${t.line}`,
            backgroundColor: t.surface,
          },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          root: { padding: "18px 20px 12px" },
          title: { fontSize: 17, fontWeight: 600, color: t.ink, letterSpacing: "-0.01em" },
          subheader: { fontSize: 13, color: t.muted, marginTop: 2 },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: t.radius.control,
            fontWeight: 500,
            transition: `background-color 160ms ${t.ease}, border-color 160ms ${t.ease}, color 160ms ${t.ease}`,
          },
          outlined: {
            borderColor: t.line,
            color: t.ink,
            backgroundColor: t.surface,
            "&:hover": { borderColor: t.lineStrong, backgroundColor: t.paper },
          },
          // Primary buttons in the company's colour (like Vanta's).
          contained: {
            backgroundColor: t.accent,
            "&:hover": { backgroundColor: alpha(t.accent, 0.88) },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: t.radius.pill, fontWeight: 500, fontSize: 12 },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: t.radius.control,
            backgroundColor: t.surface,
            "& .MuiOutlinedInput-notchedOutline": { borderColor: t.line },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: t.lineStrong },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: { color: t.lineStrong, "&.Mui-checked": { color: t.secondary } },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: { color: t.accent, textUnderlineOffset: "3px" },
        },
      },
      MuiAccordion: {
        styleOverrides: { root: { boxShadow: "none" } },
      },
      MuiDialog: {
        styleOverrides: { paper: fontRenderingWith(font) },
      },
    },
  });
};
